import { Dealer } from '@api/types';
import { GOOGLE_MAP_PROPS, SearchDealerOption } from '@components';
import { LocationState, getUserLocation } from '@store/location/location.slice';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type FindDealerContextProps = {
  children: React.ReactNode;
  fetchedDealers?: Dealer[];
};

type FindDealerContextType = {
  baseDealerList?: Dealer[];
  searchValue?: SearchDealerOption | null;
  map?: google.maps.Map | null;
  currentDealerList?: Dealer[];
  markerDetail?: Dealer | null;
  userLocation?: { lat: number; lng: number } | null;
  updateSearchValue: (value: SearchDealerOption | null) => void;
  updateInstanceMap: (mapInstance: google.maps.Map | null) => void;
  updateCurrentDealerList: (dealers: Dealer[]) => void;
  updateMarkerDetail: (dealer: Dealer | null) => void;
  requestLocation: () => void;
  resetLocation: () => void;
};

export const FindDealerContext = createContext<FindDealerContextType | null>(null);

export const FindDealerProvider: React.FC<FindDealerContextProps> = ({
  children,
  fetchedDealers,
}) => {
  const baseDealerList = useMemo(() => {
    return fetchedDealers?.filter((dealer) => dealer.longitude != null && dealer.latitude != null);
  }, [fetchedDealers]);

  const [searchValue, setSearchValue] = useState<SearchDealerOption | null>(null);
  const [markerDetail, setMarkerDetail] = useState<Dealer | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [currentDealerList, setCurrentDealerList] = useState<Dealer[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const locationState: LocationState = useSelector(getUserLocation);

  const updateCurrentDealerList = useCallback((dealers: Dealer[]) => {
    setCurrentDealerList(dealers);
  }, []);

  const updateSearchValue = useCallback((value: SearchDealerOption | null) => {
    setSearchValue(value);
  }, []);

  const updateInstanceMap = useCallback((mapInstance: google.maps.Map | null) => {
    setMap(mapInstance);
  }, []);

  const updateMarkerDetail = useCallback((marker: Dealer | null) => {
    setMarkerDetail(marker);
  }, []);

  const requestLocation = useCallback(() => {
    if (locationState.permission === 'granted' && locationState.location) {
      setLocation({
        lat: locationState.location?.latitude,
        lng: locationState.location?.longitude,
      });
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({
        lat: locationState.location.latitude,
        lng: locationState.location.longitude,
      });
      map?.fitBounds(bounds);
      map?.setZoom(GOOGLE_MAP_PROPS.midzoom);
      updateSearchValue(null);
    }
  }, [map, locationState, updateSearchValue]);

  const resetLocation = useCallback(() => {
    setLocation(null);
  }, []);

  useEffect(() => {
    if (baseDealerList) {
      updateCurrentDealerList(baseDealerList);
    }
    return () => {
      updateCurrentDealerList([]);
    };
  }, [baseDealerList, updateCurrentDealerList]);

  const contextValue: FindDealerContextType = useMemo(() => {
    return {
      updateSearchValue,
      searchValue,
      map,
      updateInstanceMap,
      currentDealerList,
      updateCurrentDealerList,
      baseDealerList,
      markerDetail,
      updateMarkerDetail,
      requestLocation,
      resetLocation,
      userLocation: location,
    };
  }, [
    updateSearchValue,
    searchValue,
    map,
    updateInstanceMap,
    currentDealerList,
    updateCurrentDealerList,
    baseDealerList,
    markerDetail,
    updateMarkerDetail,
    requestLocation,
    resetLocation,
    location,
  ]);

  return <FindDealerContext.Provider value={contextValue}>{children}</FindDealerContext.Provider>;
};

export const useFindDealerContext = () => {
  const context = useContext(FindDealerContext);

  if (!context) {
    throw new Error('useFindDealer can only be used inside DealerDetailsContextProvider');
  }

  return context;
};
